import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Helmet } from 'react-helmet-async';

const faqData = [
  {
    question: 'What is a fractional CTO and how does it work?',
    answer:
      'A fractional CTO provides executive-level technical leadership on a part-time basis, typically 1 to 4 days per week. This covers architecture decisions, technology strategy, team building, vendor management, security posture, and scaling planning. The cost is a fraction of a full-time CTO hire — typically $5K to $25K per month compared to $350K to $600K per year fully loaded for a permanent executive. The fractional model is ideal for startups and mid-market companies with $5M to $50M in revenue that need CTO-level strategic thinking without the full-time overhead.',
  },
  {
    question: 'What AI and LLM integration services does North provide?',
    answer:
      'North delivers end-to-end AI implementation services including RAG (Retrieval-Augmented Generation) pipelines, prompt engineering, and agentic workflows using platforms like OpenAI, Anthropic, and LangChain. Services also encompass enterprise AI strategy, LLM evaluation and selection, AI-powered automation of business processes, and custom AI solution development. The emphasis is always on practical, production-ready implementations that deliver measurable business value — not proofs of concept that stall after a demo.',
  },
  {
    question: 'Which cloud platforms do you work with?',
    answer:
      'North has deep expertise across AWS, Google Cloud Platform (GCP), and Microsoft Azure. Infrastructure is managed through Terraform for infrastructure as code, enabling repeatable, auditable deployments. Engagements often include multi-cloud strategy to avoid vendor lock-in, cloud migration from on-premises or legacy environments, cloud cost optimization, and cloud-native architecture design. The right platform depends on your workload, team expertise, and business goals — not vendor preferences.',
  },
  {
    question: 'How is North different from traditional consulting firms?',
    answer:
      'North is a small, senior, AI-augmented team — not a large firm that staffs engagements with junior analysts. The focus is on outcomes over headcount. The founder personally leads every engagement, providing hands-on delivery rather than strategy decks that gather dust. Knowledge transfer is built into every engagement so your team becomes more capable and self-sufficient, not dependent on ongoing consulting spend.',
  },
  {
    question: 'What industries does North serve?',
    answer:
      'North works with technology startups, SaaS companies, healthcare technology firms, financial services organizations, logistics providers, retail businesses, and any organization undergoing digital transformation. The common thread is companies that need senior technical leadership but may not need or cannot yet afford a full-time CTO. If you are scaling a product, modernizing infrastructure, or integrating AI into operations, the engagement model fits.',
  },
  {
    question: 'What does a typical engagement look like?',
    answer:
      'Most engagements start with a technology assessment and strategic roadmap to understand the current state and define priorities. From there, an ongoing fractional CTO engagement typically runs 1 to 2 days per week covering architecture reviews, team mentoring, vendor negotiations, security posture improvements, and scaling planning. Engagement length varies from focused 3-month sprints to multi-year partnerships depending on the complexity and scope of the work.',
  },
  {
    question: 'Can you help with cloud cost optimization?',
    answer:
      'Yes. Cloud cost optimization is one of the highest-ROI engagements North delivers. The process includes reserved instance and savings plan strategy, right-sizing compute and database resources, architectural changes to reduce overall compute footprint, storage tiering across hot and cold data, and vendor contract renegotiation. Typical results are 20 to 40 percent savings on cloud spend, often paying for the entire engagement within the first quarter.',
  },
  {
    question: 'Do you work with remote teams?',
    answer:
      'Yes, exclusively. The fractional CTO model is built for remote collaboration. North works with teams across North America and beyond using async communication, structured check-ins, and documented decision-making processes. The model works because fractional CTO work is strategic — it involves architecture decisions, roadmap planning, and leadership guidance that do not require daily in-person presence.',
  },
];

const FAQItem = ({ question, answer, isOpen, onToggle, index }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4, delay: index * 0.05 }}
      className="border border-gray-200 rounded-3xl overflow-hidden bg-white hover:border-gray-300 transition-colors"
    >
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-8 py-6 text-left cursor-pointer group"
        aria-expanded={isOpen}
      >
        <span className="text-lg font-semibold text-dark-slate pr-8 leading-snug">
          {question}
        </span>
        <motion.span
          animate={{ rotate: isOpen ? 45 : 0 }}
          transition={{ duration: 0.3, ease: 'easeInOut' }}
          className="flex-shrink-0 w-10 h-10 rounded-full bg-cream border border-gray-100 flex items-center justify-center text-dark-slate group-hover:bg-gray-100 transition-colors"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M8 1V15M1 8H15"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </motion.span>
      </button>

      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            key="content"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <div className="px-8 pb-8 pt-0">
              <p className="text-muted-grey leading-relaxed text-base">
                {answer}
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const handleToggle = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqData.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  };

  return (
    <section id="faq" className="py-24 px-6 bg-cream">
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(faqJsonLd)}
        </script>
      </Helmet>

      <div className="max-w-4xl mx-auto">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="inline-flex items-center px-4 py-2 bg-white rounded-full mb-6 border border-gray-100"
          >
            <span className="text-sm font-medium text-muted-grey">
              Common Questions
            </span>
          </motion.div>

          <h2 className="text-5xl md:text-6xl font-bold text-dark-slate mb-6 tracking-tight">
            Frequently Asked
            <br />
            <span className="text-muted-blue-grey">Questions</span>
          </h2>

          <p className="text-xl text-muted-grey max-w-3xl mx-auto leading-relaxed">
            Everything you need to know about fractional CTO services,
            AI consulting, and how North works with technology teams.
          </p>
        </motion.div>

        {/* FAQ Items */}
        <div className="space-y-4">
          {faqData.map((item, index) => (
            <FAQItem
              key={index}
              question={item.question}
              answer={item.answer}
              isOpen={openIndex === index}
              onToggle={() => handleToggle(index)}
              index={index}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default FAQ;
